import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Briefcase, AlertCircle, Gavel, CheckCircle, 
  Search, Plus, MoreVertical, FileText, 
  LayoutGrid, List, ArrowLeft 
} from 'lucide-react';

import { API_BASE_URL } from '../config';

const CaseDashboard = ({ onBackToEditor, onNewFiling, onOpenCase }) => {
  const [activeTab, setActiveTab] = useState('Active Cases');
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/dashboard-cases?user_id=${getUserId()}`);
      if (response.data.success) {
        setCases(response.data.cases);
      }
    } catch (error) {
      console.error("Failed to load cases", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCases = cases.filter(c =>
    c.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeCount = cases.length;
  const readyCount = cases.filter(c => c.progress >= 100).length;
  const inProgressCount = cases.filter(c => c.progress > 0 && c.progress < 100).length;

  const stats = [
    { label: 'Active Pleadings', count: activeCount, icon: Briefcase, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
    { label: 'Pending Defects', count: 0, icon: AlertCircle, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
    { label: 'In Progress', count: inProgressCount, icon: Gavel, color: 'text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/20' },
    { label: 'Ready to File', count: readyCount, icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20' },
  ];

  return (
    <div className="h-screen overflow-y-auto bg-slate-900 p-8 font-sans text-slate-100">

      {/* Header */}
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Case Dashboard</h1>
          <p className="text-slate-400 mt-2">
            {inProgressCount > 0 
              ? `You have ${inProgressCount} filing${inProgressCount > 1 ? 's' : ''} in progress.`
              : 'No active filings.'}
          </p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={onBackToEditor}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-700 hover:text-white transition-all"
          >
            <ArrowLeft size={18} />
            <span>Back to Editor</span>
          </button>
          <button 
            onClick={onNewFiling}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-all shadow-lg shadow-blue-900/20"
          >
            <Plus size={18} />
            <span>New Filing</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <div key={index} className={`bg-slate-800/50 p-5 rounded-xl border ${stat.border} shadow-sm flex items-center justify-between`}>
            <div>
              <p className="text-slate-400 text-sm font-medium mb-1">{stat.label}</p>
              <h3 className="text-3xl font-bold text-white">{stat.count}</h3>
            </div>
            <div className={`p-3 rounded-lg ${stat.bg}`}>
              <stat.icon size={24} className={stat.color} />
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-xl min-h-[500px] overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b border-slate-700">
          <div className="flex gap-6">
            {['Active Cases', 'Archived', 'Templates'].map((tab) => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`pb-4 px-1 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-blue-500 text-white'
                    : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
              <input 
                type="text" 
                placeholder="Search case..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm w-64 text-slate-200 focus:outline-none focus:border-blue-500 transition-colors"
              />
            </div>
            <div className="flex bg-slate-900 p-1 rounded-lg border border-slate-700">
              <button className="p-1.5 bg-slate-700 shadow-sm rounded text-white"><List size={16} /></button>
              <button className="p-1.5 text-slate-500 hover:text-slate-300"><LayoutGrid size={16} /></button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-slate-900/50 border-b border-slate-700 text-xs font-semibold text-slate-400 uppercase tracking-wider">
          <div className="col-span-4">Client / Case</div>
          <div className="col-span-2">Jurisdiction</div>
          <div className="col-span-2">Completion</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 flex justify-between"><span>Last Updated</span><span>Action</span></div>
        </div>

        <div className="divide-y divide-slate-700">
          {loading ? (
            <div className="px-6 py-12 text-center text-slate-500">Loading cases...</div>
          ) : filteredCases.length === 0 ? (
            <div className="px-6 py-12 text-center text-slate-500">
              No cases found. Click "New Filing" to start one.
            </div>
          ) : (
            filteredCases.map((c) => (
              <div key={c.id}
                onClick={() => onOpenCase(c.id)}
                className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-slate-700/30 transition-colors cursor-pointer"
              >
                <div className="col-span-4 flex gap-3">
                  <div className="p-2 bg-slate-700/50 rounded-lg h-fit">
                    <FileText size={20} className="text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-200 text-sm">{c.title}</h4>
                    <p className="text-xs text-slate-500 mt-0.5">{c.type}</p>
                  </div>
                </div>
                <div className="col-span-2 text-sm text-slate-400">{c.jurisdiction}</div>
                <div className="col-span-2 flex items-center gap-3">
                  <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full" style={{ width: `${c.progress}%` }}></div>
                  </div>
                  <span className="text-xs text-slate-400 w-8">{c.progress}%</span>
                </div>
                <div className="col-span-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${c.statusColor}`}>
                    {c.status}
                  </span>
                </div>
                <div className="col-span-2 flex justify-between items-center text-sm text-slate-500">
                  <span>{c.lastUpdated}</span>
                  <button className="p-1 hover:bg-slate-600 rounded text-slate-400 hover:text-white transition-all"
                    onClick={(e) => e.stopPropagation()}>
                    <MoreVertical size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseDashboard;